import { useMemo, useState } from "react";
import styles from "./index.module.scss";
import { motion } from "framer-motion";
import { BiChevronDown } from "react-icons/bi";
import ClickAwayListener from "react-click-away-listener";
import { BSFilterParamEnum } from "@/types/bridge-scanner.types";
import { BLOCKCHAINS, TEST_BLOCKCHAINS } from "@/constants/chain-details";
import { useBridgeScannerFiltersStore } from "@/features/bridge-scanner/stores/filters.store";
import BlessButton from "@/components/layout/button/green-outline-btn";
import { useFilterApply } from "@/features/bridge-scanner/hooks/useFilterApply.hook";
import CountIndicator from "@/components/layout/count-indicator";
import { useParams } from "next/navigation";
import ModularCloseBtn from "@/features/mod-perms/components/close-btn/close-btn";
import FilterOption from "@/features/bridge-scanner/components/table/left-controls/slideup-filter-menu/filter-window/filter-option";
import { TRANSFER_TYPE_OPTIONS } from "@/features/bridge-scanner/components/table/left-controls/filter";

type Props = {
  onClose: () => void;
};

const SliderFilterMenu = ({ onClose }: Props) => {
  const [filterMenuOpen, setFilterMenuOpen] =
    useState<BSFilterParamEnum | null>(null);
  const { network } = useParams() as { network: string };

  const { handleApplyParams } = useFilterApply();

  const { queryParams, setQueryParams, resetQueryParams } =
    useBridgeScannerFiltersStore();

  const handleFilterMenuOpen = (filterType: BSFilterParamEnum) => {
    if (filterMenuOpen === filterType) {
      setFilterMenuOpen(null);
    } else {
      setFilterMenuOpen(filterType);
    }
  };

  const handleClickAway = (event: any) => {
    // Prevent closing if the click is on the toggle button
    if ((event.target as HTMLElement)?.closest(`.${styles.menuButton}`)) {
      return;
    }
    setFilterMenuOpen(null);
  };

  const handleApply = () => {
    handleApplyParams();
    onClose();
  };

  const totalActiveParams = useMemo(
    () => Array.from(queryParams.values()).flatMap((i) => i).length,
    [queryParams],
  );

  const chainOptions = useMemo(
    () =>
      (network === "mainnet" ? BLOCKCHAINS : TEST_BLOCKCHAINS).map((i) => ({
        icon: i.logo,
        label: i.chainName,
        value: i.chainId.toString(),
      })),
    [network],
  );

  return (
    <div className={styles.menuWrapper}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.background}
        onClick={onClose}
      />
      <motion.div
        initial={{ y: parseInt(styles.menuHeight) }}
        animate={{ y: 0 }}
        exit={{ y: parseInt(styles.menuHeight) }}
        transition={{
          bounce: 0,
          duration: 0.1,
        }}
        className={styles.menu}
      >
        <div className={styles.header}>
          <div className={styles.title}>
            Filters
            <CountIndicator count={totalActiveParams} />
          </div>
          <div className={styles.close}>
            <ModularCloseBtn onClick={onClose} color="light" />
          </div>
        </div>
        <div className={styles.menuButtonWrapper}>
          <button
            className={styles.menuButton}
            onClick={(e) =>
              handleFilterMenuOpen(BSFilterParamEnum.TransferType)
            }
          >
            <span>
              Transfer type{" "}
              <CountIndicator
                count={queryParams.get(BSFilterParamEnum.TransferType)?.length}
              />
            </span>
            <BiChevronDown size={20} />
          </button>
          {filterMenuOpen === BSFilterParamEnum.TransferType && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className={styles.filterWindow}>
                <ul className={styles.optionsList}>
                  {TRANSFER_TYPE_OPTIONS.map((option, i) => (
                    <li key={i}>
                      <FilterOption
                        option={option}
                        selectedOptions={queryParams.get(
                          BSFilterParamEnum.TransferType,
                        )}
                        selectOption={setQueryParams}
                        filterType={BSFilterParamEnum.TransferType}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </ClickAwayListener>
          )}
        </div>
        <div className={styles.menuButtonWrapper}>
          <button
            className={styles.menuButton}
            onClick={() => handleFilterMenuOpen(BSFilterParamEnum.SourceChain)}
          >
            <span>
              Source chain{" "}
              <CountIndicator
                count={queryParams.get(BSFilterParamEnum.SourceChain)?.length}
              />
            </span>
            <BiChevronDown size={20} />
          </button>
          {filterMenuOpen === BSFilterParamEnum.SourceChain && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className={styles.filterWindow}>
                <ul className={styles.optionsList}>
                  {chainOptions.map((option, i) => (
                    <li key={i}>
                      <FilterOption
                        option={option}
                        selectedOptions={queryParams.get(
                          BSFilterParamEnum.SourceChain,
                        )}
                        selectOption={setQueryParams}
                        filterType={BSFilterParamEnum.SourceChain}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </ClickAwayListener>
          )}
        </div>
        <div className={styles.menuButtonWrapper}>
          <button
            className={styles.menuButton}
            onClick={() => handleFilterMenuOpen(BSFilterParamEnum.TargetChain)}
          >
            <span>
              Target chain{" "}
              <CountIndicator
                count={queryParams.get(BSFilterParamEnum.TargetChain)?.length}
              />
            </span>
            <BiChevronDown size={20} />
          </button>
          {filterMenuOpen === BSFilterParamEnum.TargetChain && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className={styles.filterWindow}>
                <ul className={styles.optionsList}>
                  {chainOptions.map((option, i) => (
                    <li key={i}>
                      <FilterOption
                        option={option}
                        selectedOptions={queryParams.get(
                          BSFilterParamEnum.TargetChain,
                        )}
                        selectOption={setQueryParams}
                        filterType={BSFilterParamEnum.TargetChain}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </ClickAwayListener>
          )}
        </div>
        <div className={styles.actionButtons}>
          <BlessButton styles={{ width: "100%" }} onClick={handleApply}>
            Apply filters
          </BlessButton>
          <button className={styles.resetFiltersBtn} onClick={resetQueryParams}>
            Reset filters
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SliderFilterMenu;
